<template>
    <el-dialog class="invitation-layout-dialog" title="邀请函设置" :visible="visible" width="1180px" top="4vh"
        append-to-body :close-on-click-modal="false" @update:visible="$emit('update:visible', $event)" @opened="paint">
        <div class="editor-caption">每张邀请函可单独设置。测试姓名仅用于查看效果，正式邀请函会自动显示报名用户的姓名。</div>
        <div v-loading="uploading || confirming" class="layout-editor" :element-loading-text="uploading ? '正在上传底图…' : '正在检查邀请函效果…'">
            <aside class="template-sidebar">
                <div class="sidebar-heading">邀请函底图 <span>{{ drafts.length }} / 3</span></div>
                <button v-for="(entry, index) in drafts" :key="entry.config.templatePath" type="button" class="sidebar-template"
                    :class="{ selected: selected === index }" @click="select(index)">
                    <img :src="entry.url" alt="邀请函缩略图" />
                    <span>邀请函 {{ index + 1 }}<small>{{ entry.config.mode === 'legacy' ? '沿用旧版' : entry.config.enabled ? '已设置姓名区域' : '不显示姓名' }}</small></span>
                </button>
                <el-button v-if="drafts.length < 3" class="sidebar-add" plain icon="el-icon-plus" :disabled="uploading" @click="select(-1)">添加邀请函</el-button>
                <div class="sidebar-tip">调整完成后，请先确认设置，再保存活动。</div>
            </aside>
            <main class="poster-workspace">
                <div class="workspace-toolbar">
                    <b>{{ current ? `邀请函 ${selected + 1}` : '上传邀请函底图' }}</b>
                    <div v-if="current">
                        <el-button size="mini" :loading="uploading" @click="chooseFile">更换底图</el-button>
                        <el-button size="mini" type="text" class="delete-button" @click="removeCurrent">删除</el-button>
                    </div>
                </div>
                <input ref="fileInput" type="file" class="file-input" accept="image/jpeg,image/png" @change="uploadFile" />
                <div v-if="!current" class="empty-poster">
                    <i class="el-icon-picture-outline" />
                    <h3>先上传邀请函原始底图</h3>
                    <p>1080 × 1920 · JPG / PNG · 最大 4 MB</p>
                    <el-button type="primary" :loading="uploading" @click="chooseFile">选择并上传图片</el-button>
                </div>
                <div v-else class="poster-surface">
                    <div ref="stage" class="poster-stage" :class="{ drawing: drawMode }" :style="{ aspectRatio: `${imageSize.width} / ${imageSize.height}` }"
                        @pointerdown="startDraw">
                        <img :key="current.config.templatePath" :src="current.url" alt="当前邀请函底图" draggable="false" @load="imageLoaded" @error="imageFailed" />
                        <canvas ref="canvas" :width="imageSize.width" :height="imageSize.height" />
                        <div v-if="isRegion && current.config.enabled && current.config.nameRegion" class="name-region" :style="regionStyle"
                            tabindex="0" role="button" aria-label="拖动姓名区域，方向键微调，Shift加速" @pointerdown.stop="startDrag($event, 'move')" @keydown="nudge">
                            <span class="region-label">姓名区域</span>
                            <i v-for="corner in ['nw', 'ne', 'sw', 'se']" :key="corner" class="resize-handle" :class="corner" @pointerdown.stop="startDrag($event, corner)" />
                        </div>
                    </div>
                </div>
                <div class="workspace-tip">{{ drawMode ? '在底图上按住鼠标，拖出新的姓名区域' : '拖动框调整位置，四角调整大小；方向键微调，Shift 每次移动 10 px' }}</div>
            </main>
            <aside v-if="current" class="style-panel">
                <template v-if="!isRegion">
                    <div class="panel-heading">沿用旧版</div>
                    <p class="panel-description">此图继续沿用原有的邀请函样式，查看效果不会改变已有设置。</p>
                    <el-button v-if="supported" type="primary" plain @click="enableRegion">设置姓名区域</el-button>
                    <div class="panel-heading spacing">测试姓名</div>
                    <el-input v-model="legacyPreviewName" maxlength="100" placeholder="输入预览姓名" />
                </template>
                <template v-else>
                    <div class="panel-heading">姓名设置 <el-switch v-model="current.config.enabled" /></div>
                    <div class="field-label">测试姓名</div>
                    <el-input v-model="current.config.previewName" maxlength="100" placeholder="输入姓名检查效果" />
                    <small>此姓名仅用于查看效果，不会显示在正式邀请函中</small>
                    <div class="field-label">最大字号 <span>原图 px</span></div>
                    <el-input-number v-model="current.config.textStyle.maxFontSize" :min="12" :max="300" :step="1" :precision="0" size="small" :disabled="!current.config.enabled" />
                    <div class="field-label">姓名颜色</div>
                    <el-color-picker v-model="current.config.textStyle.color" :disabled="!current.config.enabled" />
                    <div class="field-label">水平对齐</div>
                    <el-radio-group v-model="current.config.textStyle.horizontalAlign" size="mini" :disabled="!current.config.enabled">
                        <el-radio-button label="left">靠左</el-radio-button><el-radio-button label="center">居中</el-radio-button><el-radio-button label="right">靠右</el-radio-button>
                    </el-radio-group>
                    <div class="field-label"><el-checkbox v-model="current.config.textStyle.autoShrink" :disabled="!current.config.enabled">长姓名自动缩小</el-checkbox></div>
                    <div class="local-result" :class="{ overflow: !localFits }">{{ !current.config.enabled ? '此图不显示姓名' : localFits ? `参考字号 ${actualSize || '—'} px` : '姓名放不下，请扩大区域或开启自动缩小' }}</div>
                    <div class="field-label">姓名区域 <el-button type="text" :disabled="!current.config.enabled" @click="drawMode = true">重新框选</el-button></div>
                    <div v-if="current.config.nameRegion" class="coordinate-inputs">
                        <label v-for="(label, key) in coordinateLabels" :key="key"><span>{{ label }}</span><el-input-number v-model="current.config.nameRegion[key]" :min="key === 'width' || key === 'height' ? 40 : 0" :max="key === 'x' || key === 'width' ? imageSize.width : imageSize.height" :precision="0" :controls="false" size="mini" :disabled="!current.config.enabled" /></label>
                    </div>
                </template>
                <el-button v-if="supported" class="server-preview-button" icon="el-icon-view" :loading="previewLoading" :disabled="uploading || confirming || !!imageError" @click="preview">查看邀请函效果</el-button>
                <div class="panel-description">左侧为排版参考，请点击“查看邀请函效果”确认姓名的位置和大小。</div>
                <el-alert v-if="imageError" :title="imageError" type="error" :closable="false" />
            </aside>
            <aside v-else class="style-panel empty-settings"><i class="el-icon-edit-outline" /><p>上传底图后设置姓名区域</p></aside>
        </div>
        <div slot="footer" class="editor-footer">
            <span>确认后还需保存活动；保存草稿也会更新之后领取的邀请函。</span>
            <el-button @click="$emit('update:visible', false)">取消</el-button>
            <el-button type="primary" :loading="confirming" :disabled="uploading || previewLoading || !!imageError || (!drafts.length && !entries.length)" @click="confirm">确认设置</el-button>
        </div>
        <el-dialog title="邀请函效果" :visible.sync="previewVisible" width="460px" append-to-body>
            <el-alert v-if="serverResult && serverResult.fits === false" title="测试姓名超出区域，请返回扩大区域或开启自动缩小。" type="warning" :closable="false" />
            <img v-if="serverResult" class="server-image" :src="serverResult.url" alt="邀请函效果预览" />
            <div v-if="serverResult && serverResult.actualFontSize">实际字号 {{ serverResult.actualFontSize }} px</div>
        </el-dialog>
    </el-dialog>
</template>

<script>
import { copy, legacyConfig, regionConfig, templatePath, validateInvitation } from './invitationLayout';

export default {
    props: {
        visible: Boolean,
        entries: { type: Array, default: () => [] },
        initialIndex: { type: Number, default: -1 },
        activityId: { type: [Number, String], default: 0 },
        legacySettings: { type: Object, default: () => ({ invitationTemplateType: 1, posterColor: '#ffffff' }) },
        supported: { type: Boolean, default: true }
    },
    data() {
        return {
            drafts: [], selected: -1, uploading: false, confirming: false, previewLoading: false, previewVisible: false,
            verifiedPreviews: {},
            serverResult: null, previewToken: 0, session: 0, imageError: '', dimensions: {},
            legacyPreviewName: '欧阳明月', drawMode: false, drag: null, actualSize: null, localFits: true,
            coordinateLabels: { x: '距左', y: '距上', width: '宽', height: '高' }
        };
    },
    computed: {
        current() { return this.drafts[this.selected] || null; },
        isRegion() { return this.current && this.current.config.mode === 'region'; },
        imageSize() {
            if (!this.current) return { width: 1080, height: 1920 };
            return this.current.config.templateSize || this.dimensions[this.current.config.templatePath] || { width: 1080, height: 1920 };
        },
        regionStyle() {
            const r = this.current.config.nameRegion;
            return { left: `${r.x / this.imageSize.width * 100}%`, top: `${r.y / this.imageSize.height * 100}%`, width: `${r.width / this.imageSize.width * 100}%`, height: `${r.height / this.imageSize.height * 100}%` };
        }
    },
    watch: {
        visible(value) {
            this.session++;
            this.previewToken++;
            this.uploading = false;
            this.confirming = false;
            this.previewLoading = false;
            this.previewVisible = false;
            this.drag = null;
            if (value) {
                // 弹窗在深拷贝上编辑；取消、关闭和 Esc 都不改变活动表单。
                this.drafts = copy(this.entries);
                this.verifiedPreviews = {};
                this.select(this.initialIndex);
            }
        },
        drafts: { deep: true, handler() { this.invalidatePreview(); this.$nextTick(this.paint); } },
        selected() { this.invalidatePreview(); this.$nextTick(this.paint); },
        legacyPreviewName() { this.invalidatePreview(); },
        legacySettings: { deep: true, handler() { this.invalidatePreview(); } }
    },
    mounted() {
        window.addEventListener('pointermove', this.moveDrag);
        window.addEventListener('pointerup', this.endDrag);
        window.addEventListener('pointercancel', this.endDrag);
    },
    beforeDestroy() {
        window.removeEventListener('pointermove', this.moveDrag);
        window.removeEventListener('pointerup', this.endDrag);
        window.removeEventListener('pointercancel', this.endDrag);
    },
    methods: {
        select(index) { this.selected = index; this.imageError = ''; this.drawMode = false; this.drag = null; },
        invalidatePreview() { this.previewToken++; this.serverResult = null; this.previewVisible = false; },
        chooseFile() {
            if (this.uploading) return;
            this.$refs.fileInput.value = '';
            this.$refs.fileInput.click();
        },
        async uploadFile(event) {
            const file = event.target.files[0];
            if (!file) return;
            const session = this.session;
            const replacing = this.selected;
            this.uploading = true;
            let objectUrl;
            try {
                if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 4 * 1024 * 1024) throw new Error('请上传不超过4 MB的JPG或PNG图片');
                if (replacing < 0 && this.drafts.length >= 3) throw new Error('最多上传三张邀请函');
                objectUrl = URL.createObjectURL(file);
                const size = await new Promise((resolve, reject) => {
                    const image = new Image();
                    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
                    image.onerror = () => reject(new Error('无法读取图片，请重新选择'));
                    image.src = objectUrl;
                });
                if (size.width !== 1080 || size.height !== 1920) throw new Error('邀请函图片尺寸必须为1080 × 1920');
                if (session !== this.session || !this.visible) return;
                const data = new FormData();
                data.append('file', file); data.append('action', 'template'); data.append('type', 'image');
                const { data: response } = await this.$http.post(this.$store.getters.imgUrl, data, { timeout: 30000 });
                if (session !== this.session || !this.visible) return;
                if (response.errorCode !== 200 || !response.data || !response.data.url) throw new Error(response.message || '底图上传失败');
                const path = templatePath(response.data.url);
                if (this.drafts.some(entry => entry.config.templatePath === path)) throw new Error('这张底图已添加，请选择其他图片');
                const url = this.$store.state.env.cdnImg.replace(/\/$/, '') + '/' + process.env.VUE_APP_STATIC_DIR.replace(/^\/|\/$/g, '') + path;
                const entry = { name: file.name, url, config: this.supported ? regionConfig(path, size.width, size.height) : legacyConfig(path) };
                if (replacing >= 0) this.$set(this.drafts, replacing, entry);
                else { this.drafts.push(entry); this.selected = this.drafts.length - 1; }
                this.imageError = '';
            } catch (e) {
                if (session === this.session) this.$message.error(e.message || '上传失败，请重试');
            } finally {
                if (objectUrl) URL.revokeObjectURL(objectUrl);
                if (session === this.session) this.uploading = false;
            }
        },
        imageLoaded(event) {
            if (!this.current || new URL(this.current.url, window.location.href).href !== event.target.src) return;
            const size = { width: event.target.naturalWidth, height: event.target.naturalHeight };
            this.$set(this.dimensions, this.current.config.templatePath, size);
            this.imageError = this.isRegion && (size.width !== this.imageSize.width || size.height !== this.imageSize.height) ? '底图尺寸已改变，请更换底图并重新设置姓名区域' : '';
            this.paint();
        },
        imageFailed() { this.imageError = '底图加载失败，请检查图片或重新上传'; },
        enableRegion() {
            if (this.imageError) return;
            this.$set(this.current, 'config', regionConfig(this.current.config.templatePath, this.imageSize.width, this.imageSize.height));
        },
        async removeCurrent() {
            try { await this.$confirm('确定删除这张邀请函及其设置？如需撤销，请点击下方“取消”退出邀请函设置。', '删除邀请函', { type: 'warning' }); }
            catch (e) { return; }
            this.drafts.splice(this.selected, 1);
            this.select(this.drafts.length ? Math.min(this.selected, this.drafts.length - 1) : -1);
        },
        async confirm() {
            if (this.uploading || this.confirming || this.previewLoading) return;
            const session = this.session;
            try {
                validateInvitation(this.drafts, { version: 1, templates: this.drafts.map(entry => entry.config) },
                    { draft: true, supported: this.supported, requiresComplete: false, savedPaths: [], requirePreviewName: true });
                const snapshot = copy(this.drafts);
                const context = this.previewActivityContext();
                this.confirming = true;
                // 每张启用姓名的新版图都要验证当前参数；已有同参数预览可复用，未预览的图在确认时检查。
                for (let index = 0; index < snapshot.length; index++) {
                    const config = snapshot[index].config;
                    if (config.mode !== 'region' || !config.enabled) continue;
                    const key = this.previewKey(config, context);
                    const saved = this.verifiedPreviews[config.templatePath];
                    const result = saved && saved.key === key ? saved.result : await this.requestPreview(config, context);
                    if (session !== this.session || !this.visible) return;
                    if (JSON.stringify(snapshot) !== JSON.stringify(this.drafts)) throw new Error('邀请函设置已调整，请重新确认');
                    this.$set(this.verifiedPreviews, config.templatePath, { key, result });
                    if (!result.fits) {
                        this.select(index);
                        await this.$nextTick();
                        if (session !== this.session || !this.visible) return;
                        this.serverResult = result;
                        this.previewVisible = true;
                        throw new Error(`第${index + 1}张邀请函的测试姓名放不下，请扩大区域或开启自动缩小`);
                    }
                }
                if (session === this.session && this.visible) this.$emit('confirm', snapshot);
            } catch (e) {
                if (session === this.session && this.visible) this.$message.error(e.message || '暂时无法检查邀请函效果，请重试');
            } finally {
                if (session === this.session) this.confirming = false;
            }
        },
        point(event) {
            const rect = this.$refs.stage.getBoundingClientRect();
            return { x: Math.round((event.clientX - rect.left) / rect.width * this.imageSize.width), y: Math.round((event.clientY - rect.top) / rect.height * this.imageSize.height) };
        },
        startDraw(event) {
            if (!this.drawMode || !this.isRegion || !this.current.config.enabled) return;
            event.preventDefault();
            const p = this.point(event);
            this.drag = { type: 'draw', point: p, region: null, pointerId: event.pointerId };
        },
        startDrag(event, type) {
            if (this.drawMode) { this.startDraw(event); return; }
            if (!this.isRegion || !this.current.config.enabled) return;
            event.preventDefault();
            event.currentTarget.focus();
            this.drag = { type, point: this.point(event), region: copy(this.current.config.nameRegion), pointerId: event.pointerId };
        },
        moveDrag(event) {
            if (!this.drag || !this.visible || !this.current || event.pointerId !== this.drag.pointerId) return;
            event.preventDefault();
            const p = this.point(event);
            const d = this.drag;
            const maxX = this.imageSize.width; const maxY = this.imageSize.height;
            const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
            let r;
            if (d.type === 'draw') {
                const left = clamp(Math.min(p.x, d.point.x), 0, maxX - 40);
                const top = clamp(Math.min(p.y, d.point.y), 0, maxY - 40);
                r = { x: left, y: top, width: clamp(Math.abs(p.x - d.point.x), 40, maxX - left), height: clamp(Math.abs(p.y - d.point.y), 40, maxY - top) };
            } else {
                r = copy(d.region);
                const dx = p.x - d.point.x; const dy = p.y - d.point.y;
                if (d.type === 'move') { r.x = clamp(r.x + dx, 0, maxX - r.width); r.y = clamp(r.y + dy, 0, maxY - r.height); }
                else {
                    if (d.type.includes('w')) { r.x = clamp(r.x + dx, 0, d.region.x + d.region.width - 40); r.width = d.region.x + d.region.width - r.x; }
                    if (d.type.includes('e')) r.width = clamp(r.width + dx, 40, maxX - r.x);
                    if (d.type.includes('n')) { r.y = clamp(r.y + dy, 0, d.region.y + d.region.height - 40); r.height = d.region.y + d.region.height - r.y; }
                    if (d.type.includes('s')) r.height = clamp(r.height + dy, 40, maxY - r.y);
                }
            }
            this.$set(this.current.config, 'nameRegion', r);
        },
        endDrag() { this.drag = null; this.drawMode = false; },
        nudge(event) {
            if (!this.current.config.enabled || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
            event.preventDefault();
            const r = this.current.config.nameRegion; const step = event.shiftKey ? 10 : 1;
            if (event.key === 'ArrowLeft') r.x = Math.max(0, r.x - step);
            if (event.key === 'ArrowRight') r.x = Math.min(this.imageSize.width - r.width, r.x + step);
            if (event.key === 'ArrowUp') r.y = Math.max(0, r.y - step);
            if (event.key === 'ArrowDown') r.y = Math.min(this.imageSize.height - r.height, r.y + step);
        },
        paint() {
            const canvas = this.$refs.canvas;
            if (!canvas) return;
            const ctx = canvas.getContext('2d'); ctx.clearRect(0, 0, canvas.width, canvas.height);
            this.actualSize = null; this.localFits = true;
            if (!this.isRegion || !this.current.config.enabled || !this.current.config.nameRegion || !this.current.config.previewName) return;
            const config = this.current.config; const r = config.nameRegion; const style = config.textStyle;
            let size = Number(style.maxFontSize) || 12; let metrics; let width; let height;
            do {
                ctx.font = `${size}px sans-serif`; metrics = ctx.measureText(config.previewName);
                width = (metrics.actualBoundingBoxLeft || 0) + (metrics.actualBoundingBoxRight || metrics.width);
                height = (metrics.actualBoundingBoxAscent || size * 0.8) + (metrics.actualBoundingBoxDescent || size * 0.2);
                this.localFits = width <= r.width - 20 && height <= r.height - 20;
                if (this.localFits || !style.autoShrink || size <= 8) break;
                size--;
            } while (size >= 8);
            this.actualSize = size;
            if (!this.localFits) return;
            let x = r.x + 10;
            if (style.horizontalAlign === 'center') x += (r.width - 20 - width) / 2;
            if (style.horizontalAlign === 'right') x += r.width - 20 - width;
            const y = r.y + 10 + (r.height - 20 - height) / 2 + (metrics.actualBoundingBoxAscent || size * 0.8);
            ctx.fillStyle = style.color || '#163253'; ctx.textBaseline = 'alphabetic';
            ctx.fillText(config.previewName, x + (metrics.actualBoundingBoxLeft || 0), y);
        },
        /** 旧版效果依赖当前模板数量和全局样式，预览时必须携带尚未保存的表单值。 */
        previewActivityContext() {
            return {
                templatePaths: this.drafts.map(entry => entry.config.templatePath),
                invitationTemplateType: Number(this.legacySettings.invitationTemplateType),
                posterColor: this.legacySettings.posterColor || '#ffffff'
            };
        },
        previewKey(config, context) { return JSON.stringify([config, config.mode === 'legacy' ? context : null]); },
        /** 请求纯预览，不修改活动；响应必须对应本张底图并给出明确的新版适配结果。 */
        async requestPreview(config, context) {
            const { data: response } = await this.$http.post('/admin/Activity/previewInvitation', {
                activityId: this.activityId || 0, templateConfig: JSON.stringify(config),
                activityContext: JSON.stringify(context),
                ...(config.mode === 'legacy' ? { previewName: this.legacyPreviewName } : {})
            }, { timeout: 30000 });
            if (response.errorCode !== 200) throw new Error(response.message || '预览失败');
            if (!response.data || !response.data.url || response.data.templatePath !== config.templatePath) throw new Error('未能获取当前邀请函的效果，请重试');
            if (config.mode === 'region' && typeof response.data.fits !== 'boolean') throw new Error('暂时无法确认姓名是否完整显示，请重试');
            return response.data;
        },
        async preview() {
            if (this.previewLoading || this.confirming || !this.current) return;
            const config = copy(this.current.config);
            try {
                validateInvitation([this.current], { version: 1, templates: [config] }, { draft: true, supported: this.supported, savedPaths: [], requiresComplete: false, requirePreviewName: true });
            } catch (e) { this.$message.error(e.message); return; }
            this.previewLoading = true;
            const token = ++this.previewToken;
            const session = this.session;
            const context = this.previewActivityContext();
            const key = this.previewKey(config, context);
            try {
                const result = await this.requestPreview(config, context);
                if (token !== this.previewToken || !this.visible) return;
                this.$set(this.verifiedPreviews, config.templatePath, { key, result });
                this.serverResult = result; this.previewVisible = true;
            } catch (e) { if (token === this.previewToken) this.$message.error(e.message || '暂时无法查看邀请函效果，请重试'); }
            finally { if (session === this.session) this.previewLoading = false; }
        }
    }
};
</script>

<style scoped>
.editor-caption { margin-bottom:18px; color:#7b8494; font-size:13px; }
.layout-editor { display:flex; min-height:590px; border:1px solid #e5e9f0; border-radius:8px; overflow:hidden; line-height:1.5; }
.template-sidebar { width:190px; flex-shrink:0; padding:18px 12px; border-right:1px solid #e5e9f0; }
.sidebar-heading { font-weight:600; padding:0 6px 12px; }.sidebar-heading span { float:right; color:#9ca4b2; font-weight:normal; }
.sidebar-template { display:flex; align-items:center; gap:10px; width:100%; border:1px solid #e5e9ef; border-radius:6px; background:white; padding:9px; margin-bottom:12px; text-align:left; cursor:pointer; }
.sidebar-template.selected { border-color:#4778e5; background:#f1f6ff; }.sidebar-template img { width:42px; height:66px; object-fit:cover; }.sidebar-template span { color:#354059; font-size:13px; }.sidebar-template small { display:block; font-size:11px; margin-top:8px; color:#8e97a7; }
.sidebar-add { width:100%; }.sidebar-tip { color:#9ca4b2; font-size:12px; padding:18px 6px; }
.poster-workspace { flex:1; min-width:300px; display:flex; flex-direction:column; background:#f3f5f9; padding:12px 18px; }
.workspace-toolbar { display:flex; align-items:center; justify-content:space-between; height:36px; margin-bottom:10px; }.workspace-toolbar b { font-size:13px; }.delete-button { color:#f56c6c; }
.poster-surface { flex:1; display:flex; align-items:center; justify-content:center; min-height:480px; }
.poster-stage { position:relative; width:270px; max-width:100%; box-shadow:0 4px 18px #15213a20; touch-action:none; user-select:none; background:white; }
.poster-stage > img { display:block; width:100%; height:auto; pointer-events:none; }.poster-stage canvas { position:absolute; inset:0; width:100%; height:100%; pointer-events:none; }
.poster-stage.drawing { cursor:crosshair; }.name-region { position:absolute; box-sizing:border-box; border:2px solid #4778e5; background:#4778e512; cursor:move; outline:none; touch-action:none; }.name-region:focus { box-shadow:0 0 0 2px #4778e544; }.region-label { position:absolute; left:-2px; top:-23px; background:#4778e5; color:white; padding:2px 6px; font-size:10px; line-height:17px; white-space:nowrap; }
.resize-handle { position:absolute; width:8px; height:8px; border:1px solid #4778e5; background:white; }.nw { left:-5px; top:-5px; cursor:nwse-resize; }.ne { right:-5px; top:-5px; cursor:nesw-resize; }.sw { left:-5px; bottom:-5px; cursor:nesw-resize; }.se { right:-5px; bottom:-5px; cursor:nwse-resize; }
.workspace-tip { text-align:center; font-size:11px; color:#8a94a4; padding-top:14px; }
.style-panel { width:258px; flex-shrink:0; padding:18px; background:white; border-left:1px solid #e5e9f0; }.panel-heading { display:flex; justify-content:space-between; align-items:center; font-size:15px; font-weight:600; }.panel-description { color:#9099a8; font-size:12px; margin:14px 0; }.spacing { margin-top:30px; margin-bottom:12px; }.field-label { margin-top:14px; margin-bottom:7px; font-size:12px; color:#566075; display:flex; align-items:center; justify-content:space-between; }.field-label span { font-size:11px; color:#9aa3b2; }.style-panel small { color:#a0a8b5; font-size:11px; }.coordinate-inputs { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:8px; }.coordinate-inputs label { display:grid; grid-template-columns:22px minmax(0, 1fr); align-items:center; gap:6px; font-size:11px; }.coordinate-inputs label > span { white-space:nowrap; }.coordinate-inputs .el-input-number { width:100%; min-width:0; }.local-result { font-size:12px; color:#4c9979; margin-top:12px; }.local-result.overflow { color:#e6a23c; }.server-preview-button { margin-top:24px; width:100%; }.editor-footer { display:flex; align-items:center; justify-content:flex-end; gap:10px; }.editor-footer span { flex:1; text-align:left; color:#9099a8; font-size:12px; }.server-image { display:block; width:100%; margin-top:12px; }.file-input { display:none; }.empty-poster { flex:1; display:flex; align-items:center; justify-content:center; flex-direction:column; color:#9aa4b4; }.empty-poster > i { font-size:64px; color:#c5cfde; }.empty-poster h3 { color:#64728a; font-size:16px; }.empty-poster p { font-size:12px; margin-bottom:26px; }.empty-settings { display:flex; flex-direction:column; justify-content:center; align-items:center; color:#a1aab8; font-size:12px; }.empty-settings i { font-size:38px; }
</style>

<style>
.invitation-layout-dialog > .el-dialog { max-width:96vw; }.invitation-layout-dialog > .el-dialog > .el-dialog__body { padding-top:8px; }.invitation-layout-dialog .el-input-number--small { width:150px; }
</style>
